import { SettingOutlined } from '@ant-design/icons'
import {
  Button,
  Empty,
  Input,
  InputNumber,
  message,
  Skeleton,
  Table,
  Tooltip,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import { GenericPanelData } from 'features/drilldown'
import { DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA } from 'features/drilldown/components/table/DrilldownTable'
import { NII_CAMPAIGN_TITLE } from 'features/drilldown/panels/impl/niiLibraResultsTable/NiiLibraResultsPanel'
import { NiiRoiAssumptionsSettingsModal } from 'features/drilldown/panels/impl/niiRoi/NiiRoiAssumptionsSettingsModal'
import {
  formatAedFull,
  NII_CALCULATOR_CATEGORY_COLORS,
  NII_CALCULATOR_CATEGORY_LABELS,
  NII_ROI_EQUIPMENT_PREP_AED_FIELD,
  NII_ROI_INSPECTION_AED_FIELD,
  NII_ROI_SCAFFOLDING_AED_FIELD,
  NiiCalculatorSavingsCategory,
  NiiCalculatorSiteSummary,
  readNiiRoiAssetOverrides,
  ResolvedNiiCalculatorAssumptions,
} from 'features/drilldown/panels/impl/niiRoi/niiRoiCalculatorModel'
import { useNiiCampaignRoi } from 'features/drilldown/panels/impl/niiRoi/useNiiCampaignRoi'
import { createPatchData } from 'features/fde/helpers/rolodex/createPatchData'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { wrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { PermissionsGate } from 'features/permissions'
import uniqBy from 'lodash/uniqBy'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_LIBRA_EXECUTIVE,
} from 'types'

const { Text } = Typography

type NiiRoiPanelData = GenericPanelData

export const DEFAULT_NII_ROI_PANEL_DATA: NiiRoiPanelData = {
  ...DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  title: 'NII ROI',
}

const ROI_ORDER: NiiCalculatorSavingsCategory[] = [
  'equipment',
  'inspection',
  'scaffolding',
  'hse',
  'production',
]

const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
`

const KpiCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.85rem;
  background: rgba(255, 255, 255, 0.03);
`

const KpiLabel = styled.div`
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const KpiValue = styled.div`
  margin-top: 0.35rem;
  font-size: 1.55rem;
  font-weight: 700;
`

const KpiSubtext = styled.div`
  margin-top: 0.25rem;
  color: rgba(255, 255, 255, 0.48);
  font-size: 0.78rem;
`

const BreakdownCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
`

const BreakdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
`

const StackedBar = styled.div`
  display: flex;
  overflow: hidden;
  height: 1.5rem;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
`

const Segment = styled.div<{ $color: string; $widthPercent: number }>`
  width: ${({ $widthPercent }) => $widthPercent}%;
  background: ${({ $color }) => $color};
  min-width: ${({ $widthPercent }) => ($widthPercent > 0 ? '2px' : '0')};
`

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.6rem;
  margin-top: 0.75rem;
`

const CategoryCell = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: center;
  min-width: 0;
`

const Dot = styled.span<{ $color: string }>`
  width: 0.65rem;
  height: 0.65rem;
  flex: 0 0 auto;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`

const CategoryText = styled.div`
  min-width: 0;
`

const SiteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 0.75rem;
`

const SiteCard = styled.button<{ $selected: boolean }>`
  width: 100%;
  color: inherit;
  text-align: left;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? 'rgba(91, 155, 213, 0.85)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 8px;
  padding: 0.85rem;
  background: ${({ $selected }) =>
    $selected ? 'rgba(91, 155, 213, 0.14)' : 'transparent'};
  cursor: pointer;

  &:hover {
    border-color: rgba(91, 155, 213, 0.7);
  }
`

const SiteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`

const SiteStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem 0.75rem;
  margin-top: 0.65rem;
  font-size: 0.8rem;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.5rem;
`

type AssetOverrideEdit = {
  equipmentPrepMobDemobAedPerAsset?: number | null
  niiInspectionAedPerAsset?: number | null
  scaffoldingAedPerAsset?: number | null
}

type AssetOverrideRow = {
  ticket: LibraTicket
  flo: string
  title: string
  equipmentPrepMobDemobAedPerAsset?: number
  niiInspectionAedPerAsset?: number
  scaffoldingAedPerAsset?: number
}

const { Search } = Input

const ASSET_OVERRIDE_FIELD_BY_EDIT_KEY: Record<
  keyof AssetOverrideEdit,
  string
> = {
  equipmentPrepMobDemobAedPerAsset: NII_ROI_EQUIPMENT_PREP_AED_FIELD,
  niiInspectionAedPerAsset: NII_ROI_INSPECTION_AED_FIELD,
  scaffoldingAedPerAsset: NII_ROI_SCAFFOLDING_AED_FIELD,
}

function formatCompactAed(value: number): string {
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)
  if (abs >= 1_000_000_000)
    return `${sign}AED ${(abs / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${sign}AED ${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}AED ${(abs / 1_000).toFixed(0)}K`
  return `${sign}AED ${Math.round(abs).toLocaleString()}`
}

function percent(numerator: number, denominator: number): string {
  if (denominator <= 0) return '0%'
  return `${Math.round((numerator / denominator) * 100)}%`
}

function siteName(
  siteId: string,
  siteOptions: ReadonlyArray<{ value: string; label: string }>
): string {
  return siteOptions.find((option) => option.value === siteId)?.label ?? siteId
}

function ticketSiteId(ticket: LibraTicket): string | undefined {
  return typeof ticket.tags.site === 'string' && ticket.tags.site
    ? ticket.tags.site
    : undefined
}

const SiteSummaryCard: FC<{
  site: NiiCalculatorSiteSummary
  siteLabel: string
  selected: boolean
  onSelect: () => void
}> = ({ site, siteLabel, selected, onSelect }) => {
  return (
    <SiteCard type='button' $selected={selected} onClick={onSelect}>
      <SiteHeader>
        <div>
          <Text strong>{siteLabel}</Text>
          <KpiSubtext>{site.eligibleAssets} eligible assets</KpiSubtext>
        </div>
        <Text strong>{formatCompactAed(site.breakdown.total)}</Text>
      </SiteHeader>
      <SiteStats>
        <span>Reviewed</span>
        <Text>{site.reviewedAssets}</Text>
        <span>Complete</span>
        <Text>{site.completeAssets}</Text>
        <span>In progress</span>
        <Text>{site.inProgressAssets}</Text>
        <span>Per eligible asset</span>
        <Text>{formatCompactAed(site.savingPerEligibleAsset)}</Text>
      </SiteStats>
    </SiteCard>
  )
}

function buildAssetRows(
  tickets: ReadonlyArray<LibraTicket>
): AssetOverrideRow[] {
  return uniqBy(tickets, (ticket) =>
    typeof ticket.tags.unit === 'string' && ticket.tags.unit
      ? ticket.tags.unit
      : ticket.id
  ).map((ticket) => {
    const overrides = readNiiRoiAssetOverrides(ticket)
    return {
      ticket,
      flo: typeof ticket.tags.unit === 'string' ? ticket.tags.unit : ticket.id,
      title: ticket.title,
      ...overrides,
    }
  })
}

const AssetOverrideTable: FC<{
  tickets: ReadonlyArray<LibraTicket>
  siteLabel: string
  siteAssumptions: ResolvedNiiCalculatorAssumptions
  onSaved: () => void
}> = ({ tickets, siteLabel, siteAssumptions, onSaved }) => {
  const { rolodexPatch } = useRolodexEdit()
  const [edits, setEdits] = useState<Record<string, AssetOverrideEdit>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [searchText, setSearchText] = useState('')

  const rows = useMemo(() => buildAssetRows(tickets), [tickets])
  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase()
    if (!normalizedSearch) return rows
    return rows.filter((row) =>
      [row.flo, row.title].some((value) =>
        value.toLowerCase().includes(normalizedSearch)
      )
    )
  }, [rows, searchText])
  const hasEdits = Object.keys(edits).length > 0

  const setEdit = useCallback(
    (
      ticketId: string,
      field: keyof AssetOverrideEdit,
      value: number | null
    ) => {
      setEdits((prev) => ({
        ...prev,
        [ticketId]: {
          ...(prev[ticketId] ?? {}),
          [field]: value,
        },
      }))
    },
    []
  )

  const readEditedValue = useCallback(
    (
      row: AssetOverrideRow,
      field: keyof AssetOverrideEdit
    ): number | null | undefined => {
      const edited = edits[row.ticket.id]?.[field]
      if (edited !== undefined) return edited
      return row[field]
    },
    [edits]
  )

  const saveEdits = useCallback(async () => {
    setIsSaving(true)
    try {
      const patches = Object.entries(edits).flatMap(([ticketId, edit]) => {
        const ticket = tickets.find((item) => item.id === ticketId)
        if (!ticket) return []

        const nextFieldValues: FieldValues = { ...ticket.fieldValues }
        Object.entries(ASSET_OVERRIDE_FIELD_BY_EDIT_KEY).forEach(
          ([editKey, fieldId]) => {
            const value = edit[editKey as keyof AssetOverrideEdit]
            if (value === undefined) return
            if (value === null) {
              delete nextFieldValues[fieldId]
              return
            }
            nextFieldValues[fieldId] = wrapFieldValue(value)
          }
        )

        const patch = createPatchData(
          ticket.id,
          { fieldValues: ticket.fieldValues },
          { fieldValues: nextFieldValues }
        )
        return patch ? [patch] : []
      })

      if (patches.length > 0) {
        await rolodexPatch(patches)
      }
      setEdits({})
      onSaved()
      message.success('Asset ROI costs saved')
    } catch (error) {
      console.error(error)
      message.error('Could not save asset ROI costs')
    } finally {
      setIsSaving(false)
    }
  }, [edits, onSaved, rolodexPatch, tickets])

  const columns = useMemo<ColumnsType<AssetOverrideRow>>(
    () => [
      {
        title: 'FLO',
        dataIndex: 'flo',
        width: 160,
      },
      {
        title: 'Asset',
        dataIndex: 'title',
        width: 240,
      },
      {
        title: 'Equipment savings',
        render: (_, row) => (
          <InputNumber
            min={0}
            value={readEditedValue(row, 'equipmentPrepMobDemobAedPerAsset')}
            placeholder={formatCompactAed(
              siteAssumptions.equipmentPrepMobDemobAedPerAsset
            )}
            onChange={(value) =>
              setEdit(row.ticket.id, 'equipmentPrepMobDemobAedPerAsset', value)
            }
            style={{ width: '100%' }}
          />
        ),
      },
      {
        title: 'Inspection cost',
        render: (_, row) => (
          <InputNumber
            min={0}
            value={readEditedValue(row, 'niiInspectionAedPerAsset')}
            placeholder={formatCompactAed(
              siteAssumptions.niiInspectionAedPerAsset
            )}
            onChange={(value) =>
              setEdit(row.ticket.id, 'niiInspectionAedPerAsset', value)
            }
            style={{ width: '100%' }}
          />
        ),
      },
      {
        title: 'Scaffolding savings',
        render: (_, row) => (
          <InputNumber
            min={0}
            value={readEditedValue(row, 'scaffoldingAedPerAsset')}
            placeholder={formatCompactAed(
              siteAssumptions.scaffoldingAedPerAsset
            )}
            onChange={(value) =>
              setEdit(row.ticket.id, 'scaffoldingAedPerAsset', value)
            }
            style={{ width: '100%' }}
          />
        ),
      },
    ],
    [readEditedValue, setEdit, siteAssumptions]
  )

  return (
    <div>
      <SectionHeader>
        <div>
          <KpiLabel>Asset Contract Costs</KpiLabel>
          <KpiSubtext>
            {siteLabel}. Blank cells use the default shown in the placeholder.
            Entered values are used for that asset; clearing a cell returns it
            to the site/TAR default.
          </KpiSubtext>
        </div>
        <Button
          type='primary'
          disabled={!hasEdits}
          loading={isSaving}
          onClick={saveEdits}>
          Save Asset Costs
        </Button>
      </SectionHeader>
      <Search
        allowClear
        placeholder='Search assets or FLOs'
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        style={{ marginBottom: 8, maxWidth: 360 }}
      />
      <Table<AssetOverrideRow>
        rowKey={(row) => row.ticket.id}
        columns={columns}
        dataSource={filteredRows}
        pagination={{ pageSize: 10 }}
        size='small'
        scroll={{ x: 'max-content' }}
      />
    </div>
  )
}

export const NiiRoiPanel: FC = () => {
  const {
    tickets,
    group,
    roi,
    siteOptions,
    siteIds,
    isLoading,
    refreshTickets,
    refreshGroup,
  } = useNiiCampaignRoi()
  const [assumptionsOpen, setAssumptionsOpen] = useState(false)
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>()

  const positiveCategoryTotal = useMemo(
    () =>
      ROI_ORDER.reduce(
        (sum, category) => sum + Math.max(0, roi.byCategory[category]),
        0
      ),
    [roi.byCategory]
  )

  useEffect(() => {
    if (
      selectedSiteId &&
      roi.bySite.some((site) => site.siteId === selectedSiteId)
    ) {
      return
    }
    setSelectedSiteId(roi.bySite[0]?.siteId)
  }, [roi.bySite, selectedSiteId])

  const selectedSiteLabel = selectedSiteId
    ? siteName(selectedSiteId, siteOptions)
    : 'Select a turnaround'
  const selectedSite = roi.bySite.find((site) => site.siteId === selectedSiteId)
  const selectedSiteTickets = useMemo(
    () =>
      selectedSiteId
        ? tickets.filter((ticket) => ticketSiteId(ticket) === selectedSiteId)
        : [],
    [selectedSiteId, tickets]
  )

  if (isLoading) {
    return (
      <PanelBody>
        <Skeleton active paragraph={{ rows: 8 }} />
      </PanelBody>
    )
  }

  if (!group) {
    return (
      <Empty
        description={`No Libra ticket group found for ${NII_CAMPAIGN_TITLE}`}
      />
    )
  }

  return (
    <PanelBody>
      <HeaderRow>
        <div>
          <Text type='secondary'>{NII_CAMPAIGN_TITLE}</Text>
          <KpiSubtext>
            ROI reflects current Explorer filters and latest complete tickets.
          </KpiSubtext>
        </div>
        <PermissionsGate
          requires={[
            PERMISSION_GECKO_INTERNAL_MEMBER,
            PERMISSION_LIBRA_EXECUTIVE,
          ]}>
          <Button
            type='text'
            icon={<SettingOutlined />}
            onClick={() => {
              if (siteIds.length === 0) {
                message.info('Add tickets with a site tag first.')
                return
              }
              setAssumptionsOpen(true)
            }}>
            Site Assumptions
          </Button>
        </PermissionsGate>
      </HeaderRow>

      <KpiGrid>
        <KpiCard>
          <KpiLabel>NII Eligible Assets</KpiLabel>
          <KpiValue>{roi.eligibleAssets}</KpiValue>
          <KpiSubtext>
            {percent(roi.eligibleAssets, roi.reviewedAssets)} eligibility rate
          </KpiSubtext>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Total Reviewed</KpiLabel>
          <KpiValue>{roi.reviewedAssets}</KpiValue>
          <KpiSubtext>{roi.completeAssets} complete</KpiSubtext>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Projected NII Saving</KpiLabel>
          <KpiValue>{formatCompactAed(roi.byCategory.total)}</KpiValue>
          <KpiSubtext>Conservative estimate</KpiSubtext>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Saving Per Asset</KpiLabel>
          <KpiValue>{formatCompactAed(roi.savingPerEligibleAsset)}</KpiValue>
          <KpiSubtext>Per eligible asset</KpiSubtext>
        </KpiCard>
      </KpiGrid>

      <BreakdownCard>
        <BreakdownHeader>
          <KpiLabel>Projected NII Campaign ROI Breakdown</KpiLabel>
          <Text strong>{formatCompactAed(roi.byCategory.total)}</Text>
        </BreakdownHeader>
        <StackedBar>
          {ROI_ORDER.map((category) => {
            const value = roi.byCategory[category]
            const widthPercent =
              positiveCategoryTotal > 0
                ? (Math.max(0, value) / positiveCategoryTotal) * 100
                : 100 / ROI_ORDER.length
            return (
              <Tooltip
                key={category}
                title={`${NII_CALCULATOR_CATEGORY_LABELS[category]}: ${formatAedFull(
                  value
                )}`}>
                <Segment
                  $color={NII_CALCULATOR_CATEGORY_COLORS[category]}
                  $widthPercent={widthPercent}
                />
              </Tooltip>
            )
          })}
        </StackedBar>
        <CategoryGrid>
          {ROI_ORDER.map((category) => (
            <CategoryCell key={category}>
              <Dot $color={NII_CALCULATOR_CATEGORY_COLORS[category]} />
              <CategoryText>
                <Text type='secondary'>
                  {NII_CALCULATOR_CATEGORY_LABELS[category]}
                </Text>
                <br />
                <Text>{formatCompactAed(roi.byCategory[category])}</Text>
              </CategoryText>
            </CategoryCell>
          ))}
        </CategoryGrid>
      </BreakdownCard>

      <div>
        <KpiLabel>Turnarounds</KpiLabel>
        <SiteGrid>
          {roi.bySite.map((site) => (
            <SiteSummaryCard
              key={site.siteId}
              site={site}
              siteLabel={siteName(site.siteId, siteOptions)}
              selected={site.siteId === selectedSiteId}
              onSelect={() => setSelectedSiteId(site.siteId)}
            />
          ))}
        </SiteGrid>
      </div>

      {selectedSiteId && selectedSite ? (
        <AssetOverrideTable
          tickets={selectedSiteTickets}
          siteLabel={selectedSiteLabel}
          siteAssumptions={selectedSite.assumptions}
          onSaved={refreshTickets}
        />
      ) : (
        <Empty description='Select a turnaround to edit asset costs.' />
      )}

      <NiiRoiAssumptionsSettingsModal
        open={assumptionsOpen}
        onClose={() => setAssumptionsOpen(false)}
        group={group}
        siteOptions={siteOptions}
        selectedSiteId={undefined}
        onSaved={refreshGroup}
      />
    </PanelBody>
  )
}
